<template>
  <div class="discover-manage-page">
    <header class="page-hero">
      <div>
        <span class="eyebrow">Discover Works</span>
        <h2>公开作品管理</h2>
        <p>管理发现页公开视频与图片，支持筛选、预览、推荐与内容编辑。</p>
      </div>
      <div class="hero-stats">
        <div>
          <strong>{{ total }}</strong>
          <span>公开作品</span>
        </div>
        <div>
          <strong>{{ recommendCount }}</strong>
          <span>本页推荐</span>
        </div>
      </div>
    </header>

    <section class="content-panel">
      <div class="toolbar">
        <div class="toolbar-left">
          <el-segmented v-model="typeFilter" :options="typeOptions" class="type-segment" @change="handleFilterChange" />
          <el-select v-model="suggestFilter" class="suggest-select" placeholder="推荐筛选" @change="handleFilterChange">
            <el-option label="全部作品" value="" />
            <el-option label="仅推荐" value="1" />
          </el-select>
          <el-input v-model="keyword" clearable class="keyword-input" placeholder="搜索提示词或作品ID" @keyup.enter="handleFilterChange">
            <template #prefix>
              <el-icon>
                <Search />
              </el-icon>
            </template>
          </el-input>
        </div>
        <el-button class="ghost-btn" :loading="loading" @click="getVideoList">
          <el-icon>
            <Refresh />
          </el-icon>
          刷新
        </el-button>
      </div>

      <div v-if="loading" class="loading-grid">
        <el-skeleton v-for="item in 6" :key="item" animated>
          <template #template>
            <div class="skeleton-card">
              <el-skeleton-item variant="image" class="skeleton-cover" />
              <el-skeleton-item variant="h3" style="width: 70%" />
              <el-skeleton-item variant="text" />
              <el-skeleton-item variant="text" style="width: 55%" />
            </div>
          </template>
        </el-skeleton>
      </div>

      <el-empty v-else-if="!videoList.length" description="暂无公开作品" />

      <div v-else class="work-grid">
        <article v-for="item in videoList" :key="item.id" class="work-card">
          <div class="cover-box">
            <video v-if="item.type === 'video'" :src="item.url" :poster="item.cover" controls preload="metadata" />
            <el-image v-else :src="item.url" fit="cover" :preview-src-list="[item.url]" preview-teleported>
              <template #error>
                <div class="image-fallback">无图</div>
              </template>
            </el-image>
            <span class="type-badge">{{ item.type === 'video' ? '视频' : '图片' }}</span>
            <span v-if="item.suggest == 1" class="recommend-badge">推荐</span>
          </div>

          <div class="work-body">
            <div class="work-meta">
              <span>ID {{ item.id }}</span>
              <span>{{ item.model || '未知模型' }}</span>
              <span>{{ item.resolution || '默认清晰度' }}</span>
            </div>
            <p>{{ item.prompt || '暂无提示词' }}</p>
            <div class="work-actions">
              <el-button class="ghost-btn" size="small" @click="openEditDialog(item)">编辑作品</el-button>
            </div>
          </div>
        </article>
      </div>

      <div class="pager-row">
        <el-pagination v-model:current-page="page" v-model:page-size="limit" :total="total"
          layout="total, sizes, prev, pager, next, jumper" :page-sizes="[9, 12, 18, 24]"
          @size-change="handlePageSizeChange" @current-change="getVideoList" />
      </div>
    </section>

    <el-dialog v-model="editDialogVisible" title="编辑公开作品" width="760px" class="discover-dialog" destroy-on-close>
      <div class="dialog-layout">
        <div class="dialog-preview">
          <video v-if="editForm.type === 'video'" :src="editForm.url" :poster="previewCover" controls preload="metadata" />
          <el-image v-else :src="editForm.url" fit="cover" :preview-src-list="[editForm.url]" preview-teleported />
        </div>

        <el-form ref="editFormRef" :model="editForm" label-position="top" class="edit-form">
          <div class="form-row">
            <el-form-item label="作品类型">
              <el-tag effect="dark" :type="editForm.type === 'video' ? 'primary' : 'success'">
                {{ editForm.type === 'video' ? '视频' : '图片' }}
              </el-tag>
            </el-form-item>
            <el-form-item label="推荐状态">
              <el-switch v-model="editForm.suggest" active-value="1" inactive-value="0" active-text="推荐"
                inactive-text="普通" />
            </el-form-item>
          </div>

          <el-form-item label="提示词" prop="prompt" required>
            <el-input v-model="editForm.prompt" type="textarea" :rows="7" maxlength="1000" show-word-limit
              resize="none" placeholder="请输入展示在发现页的作品提示词" />
          </el-form-item>

          <el-form-item v-if="editForm.type === 'video'" label="更换视频">
            <el-upload class="video-uploader" action="#" :auto-upload="false" :show-file-list="false"
              accept="video/mp4,video/mov,video/quicktime" :on-change="handleVideoUpload">
              <div class="upload-box">
                <el-icon>
                  <Upload />
                </el-icon>
                <div>
                  <b>上传新视频</b>
                  <span>提交后转存正式存储，并自动截取封面</span>
                </div>
              </div>
            </el-upload>
          </el-form-item>

          <el-form-item v-if="previewCover" label="封面预览">
            <el-image :src="previewCover" class="preview-img" fit="cover" :preview-src-list="[previewCover]"
              preview-teleported />
          </el-form-item>
        </el-form>
      </div>

      <template #footer>
        <el-button class="ghost-btn" @click="closeEditDialog">取消</el-button>
        <el-button type="primary" class="submit-btn" :loading="submitLoading" @click="submitEdit">确认保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh, Search, Upload } from '@element-plus/icons-vue'
import axios from 'axios'
import { API_URL, COSURL, upfile } from '@/util/urls'

const loading = ref(false)
const submitLoading = ref(false)
const editDialogVisible = ref(false)
const page = ref(1)
const limit = ref(12)
const total = ref(0)
const videoList = ref([])
const typeFilter = ref('video')
const suggestFilter = ref('')
const keyword = ref('')

const previewCover = ref('')
const newVideoFile = ref(null)
const newCoverBlob = ref(null)

const typeOptions = [
  { label: '视频', value: 'video' },
  { label: '图片', value: 'image' }
]

const editForm = reactive({
  id: '',
  type: '',
  prompt: '',
  suggest: '0',
  url: '',
  cover: ''
})

const recommendCount = computed(() => videoList.value.filter(item => item.suggest == 1).length)

const getVideoList = async () => {
  loading.value = true
  try {
    const params = {
      disclosure: 1,
      page: page.value,
      count: limit.value,
      type: typeFilter.value,
      suggest: suggestFilter.value,
      keyword: keyword.value.trim()
    }
    const res = await axios.get(`${API_URL}/api/gettoolworks`, { params })
    if (res.data.msg === 'success') {
      videoList.value = res.data.data || []
      total.value = Number(res.data.total || 0)
    } else {
      ElMessage.error(res.data.error || '获取列表失败')
    }
  } catch (err) {
    ElMessage.error('获取列表失败')
  } finally {
    loading.value = false
  }
}

const handleFilterChange = () => {
  page.value = 1
  getVideoList()
}

const handlePageSizeChange = () => {
  page.value = 1
  getVideoList()
}

const openEditDialog = (row) => {
  editForm.id = row.id
  editForm.type = row.type
  editForm.prompt = row.prompt || ''
  editForm.suggest = row.suggest == 1 ? '1' : '0'
  editForm.url = row.url || ''
  editForm.cover = row.cover || ''
  previewCover.value = row.type === 'video' ? row.cover : row.url
  newVideoFile.value = null
  newCoverBlob.value = null
  editDialogVisible.value = true
}

const closeEditDialog = () => {
  editDialogVisible.value = false
  previewCover.value = ''
  newVideoFile.value = null
  newCoverBlob.value = null
}

const handleVideoUpload = async (uploadFile) => {
  const file = uploadFile.raw
  if (!file) return
  newVideoFile.value = file

  try {
    const localVideoUrl = URL.createObjectURL(file)
    const coverBlob = await upfile.getVideoCover(localVideoUrl)
    URL.revokeObjectURL(localVideoUrl)
    newCoverBlob.value = coverBlob
    previewCover.value = URL.createObjectURL(coverBlob)
    ElMessage.success('视频加载和封面截取成功')
  } catch (err) {
    ElMessage.error(`操作失败：${err.message || '封面截取失败'}`)
  }
}

const submitEdit = async () => {
  if (!editForm.prompt.trim()) return ElMessage.warning('请输入提示词')

  submitLoading.value = true
  try {
    let finalVideoUrl = editForm.url
    let finalCoverUrl = editForm.cover || editForm.url

    if (editForm.type === 'video' && newVideoFile.value && newCoverBlob.value) {
      const phone = localStorage.getItem('userPhone') || 'admin'
      const token = await upfile.getuptoken(phone, 'video')
      const videoQiniu = await upfile.uploadFile(token, newVideoFile.value)

      const ctoken = await upfile.getuptoken(phone, 'image')
      const coverFile = new File([newCoverBlob.value], `cover_${editForm.id}.jpg`, { type: 'image/jpeg' })
      const coverQiniu = await upfile.uploadFile(ctoken, coverFile)

      finalVideoUrl = `${COSURL}/${videoQiniu.key}`
      finalCoverUrl = `${COSURL}/${coverQiniu.key}`
    }

    const res = await axios.post(`${API_URL}/api/updateVideoPublic`, {
      id: editForm.id,
      prompt: editForm.prompt.trim(),
      url: finalVideoUrl,
      cover: finalCoverUrl,
      suggest: editForm.suggest
    })

    if (res.data.msg === 'success') {
      ElMessage.success('保存成功')
      closeEditDialog()
      getVideoList()
    } else {
      ElMessage.error(res.data.error || '保存失败')
    }
  } catch (err) {
    ElMessage.error(`保存失败：${err.message || err}`)
  } finally {
    submitLoading.value = false
  }
}

getVideoList()
</script>

<style lang="scss" scoped>
.discover-manage-page {
  height: 100%;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
  padding: 24px;
  background: #070b12;
  color: #e8edf7;
  display: flex;
  flex-direction: column;
}

:global(.el-main:has(.discover-manage-page)) {
  height: calc(100vh - 60px);
  padding: 0;
  overflow: hidden;
  background: #070b12;
}

.page-hero {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-end;
  padding: 24px;
  border: 1px solid #202938;
  border-radius: 10px;
  background: linear-gradient(135deg, #111827 0%, #0b111d 58%, #131018 100%);
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.28);
  margin-bottom: 18px;

  h2 {
    margin: 6px 0 8px;
    color: #ffffff;
    font-size: 28px;
    line-height: 1.15;
  }

  p {
    margin: 0;
    color: #8f9bad;
  }
}

.eyebrow {
  color: #ff9b4a;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.hero-stats {
  display: flex;
  gap: 12px;

  div {
    width: 112px;
    padding: 14px;
    border: 1px solid #263142;
    border-radius: 8px;
    background: rgba(10, 15, 24, 0.76);
  }

  strong,
  span {
    display: block;
  }

  strong {
    margin-bottom: 4px;
    color: #ffffff;
    font-size: 24px;
  }

  span {
    color: #8f9bad;
    font-size: 12px;
  }
}

.content-panel {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border: 1px solid #202938;
  border-radius: 10px;
  background: #0d131f;
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.24);
  padding: 18px;
}

.toolbar {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding-bottom: 16px;
  background: #0d131f;
}

.toolbar-left {
  min-width: 0;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.type-segment {
  --el-segmented-bg-color: #151d2b;
  --el-segmented-item-selected-bg-color: rgba(255, 116, 23, 0.18);
  --el-segmented-item-selected-color: #ff9b4a;
  --el-segmented-item-hover-bg-color: rgba(255, 255, 255, 0.05);
}

.suggest-select {
  width: 130px;
}

.keyword-input {
  width: 260px;
}

.ghost-btn,
.submit-btn {
  border-radius: 6px;
}

.ghost-btn {
  border-color: #314059;
  background: #121a28;
  color: #d6deea;

  &:hover {
    border-color: #ff7417;
    color: #ff9b4a;
    background: #161f2f;
  }
}

.work-grid,
.loading-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
  gap: 14px;
}

.work-card {
  overflow: hidden;
  border: 1px solid #202938;
  border-radius: 10px;
  background: #101827;
  transition: transform 0.18s, border-color 0.18s, background 0.18s;

  &:hover {
    transform: translateY(-2px);
    border-color: #35445d;
    background: #121c2b;
  }
}

.cover-box {
  position: relative;
  aspect-ratio: 16 / 9;
  background: #060b13;

  video,
  .el-image {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
}

.type-badge,
.recommend-badge {
  position: absolute;
  top: 10px;
  padding: 4px 8px;
  border-radius: 5px;
  font-size: 12px;
  color: #ffffff;
}

.type-badge {
  left: 10px;
  background: rgba(37, 99, 235, 0.85);
}

.recommend-badge {
  right: 10px;
  background: rgba(245, 158, 11, 0.9);
}

.work-body {
  padding: 12px;
}

.work-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;

  span {
    color: #8f9bad;
    font-size: 12px;
  }
}

.work-body p {
  height: 64px;
  margin: 0 0 12px;
  color: #d4dceb;
  font-size: 13px;
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.work-actions {
  display: flex;
  justify-content: flex-end;
}

.pager-row {
  display: flex;
  justify-content: flex-end;
  padding-top: 18px;
}

.skeleton-card {
  padding: 12px;
  border: 1px solid #202938;
  border-radius: 10px;
  background: #101827;
}

.skeleton-cover {
  width: 100%;
  height: 168px;
  margin-bottom: 12px;
}

.dialog-layout {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 18px;
}

.dialog-preview {
  border: 1px solid #263142;
  border-radius: 8px;
  background: #060b13;
  overflow: hidden;
  aspect-ratio: 16 / 9;

  video,
  .el-image {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.upload-box {
  width: 100%;
  min-height: 76px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  box-sizing: border-box;
  border: 1px dashed #324058;
  border-radius: 8px;
  background: #101827;
  color: #d6deea;
  cursor: pointer;

  .el-icon {
    color: #ff9b4a;
    font-size: 26px;
  }

  b,
  span {
    display: block;
  }

  span {
    margin-top: 4px;
    color: #8f9bad;
    font-size: 12px;
  }
}

.video-uploader,
.video-uploader :deep(.el-upload) {
  width: 100%;
}

.preview-img {
  width: 220px;
  height: 124px;
  border-radius: 8px;
  border: 1px solid #263142;
}

.image-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #788397;
}

:deep(.el-input__wrapper),
:deep(.el-select__wrapper),
:deep(.el-textarea__inner) {
  background: #121a28;
  border: 1px solid #2b374b;
  box-shadow: none;
  color: #e8edf7;
}

:deep(.el-input__wrapper:hover),
:deep(.el-select__wrapper:hover),
:deep(.el-textarea__inner:hover),
:deep(.el-input__wrapper.is-focus),
:deep(.el-select__wrapper.is-focused),
:deep(.el-textarea__inner:focus) {
  border-color: #ff7417;
}

:deep(.el-input__inner),
:deep(.el-textarea__inner),
:deep(.el-select__placeholder),
:deep(.el-select__selected-item) {
  color: #e8edf7;
}

:deep(.el-form-item__label) {
  color: #cbd5e1;
  font-weight: 600;
}

:deep(.el-input__count),
:deep(.el-input__count-inner),
:deep(.el-textarea .el-input__count),
:deep(.el-textarea .el-input__count-inner) {
  background: transparent !important;
  color: #667286;
}

:deep(.el-pagination) {
  --el-pagination-bg-color: #121a28;
  --el-pagination-button-disabled-bg-color: #0d131f;
  --el-pagination-hover-color: #ff9b4a;
  --el-pagination-text-color: #cbd5e1;
  --el-pagination-button-color: #cbd5e1;
}

:deep(.el-empty__description p) {
  color: #8f9bad;
}

@media (max-width: 1180px) {
  .dialog-layout {
    grid-template-columns: 1fr;
  }

  .toolbar {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
